// 잡 목록 표시 변환(순수) — BulkJobList 가 사용. 시간은 항상 KST 로 표기.
const KST_FMT = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function fmtDateTimeKST(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const parts = KST_FMT.formatToParts(d);
  const g = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${g("month")}-${g("day")} ${g("hour")}:${g("minute")}`;
}

export function sourceBadge(source) {
  return source === "api" ? "API" : "파일";
}

export function parseProgress(p) {
  if (!p) return null;
  if (typeof p === "object") return p;
  try {
    return JSON.parse(p);
  } catch (e) {
    return null;
  }
}

export function progressText(progress, status) {
  if (status !== "RUNNING" && status !== "PENDING") return "";
  const done = progress?.done ?? 0;
  const total = progress?.total ?? 0;
  return total ? `${done}/${total}` : "";
}

/** 목록 응답 행 → 표시용 뷰모델. orig=원본 행수 합(items[].count — dedup 전, 구 잡은 0=미표기). */
export function jobRowView(j) {
  return {
    jobId: j.jobId,
    badge: sourceBadge(j.source),
    isApi: j.source === "api",
    label: j.label || "(무제)",
    when: fmtDateTimeKST(j.createdAt),
    sender: j.userId || "",
    count: j.itemCount ?? 0,
    orig: Number(j.origCount) || 0,
    status: j.status || "",
    progress: progressText(parseProgress(j.progress), j.status),
  };
}

// ── 이력 필터 보조(순수) ──────────────────────────────────────────────

const DAY_MS = 24 * 3600 * 1000;
function kstYmd(date) {
  const k = new Date(date.getTime() + 9 * 3600 * 1000); // KST 시프트 후 UTC 게터
  const mm = String(k.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(k.getUTCDate()).padStart(2, "0");
  return `${k.getUTCFullYear()}-${mm}-${dd}`;
}

/** 프리셋 기간(KST) — kind: today | week(최근7일) | month(이번달 1일~오늘). */
export function presetRange(kind, now = new Date()) {
  const today = kstYmd(now);
  if (kind === "today") return { from: today, to: today };
  if (kind === "week")
    return { from: kstYmd(new Date(now.getTime() - 6 * DAY_MS)), to: today };
  if (kind === "month") return { from: `${today.slice(0, 7)}-01`, to: today };
  return { from: "", to: "" };
}

/** 목록 조회 쿼리스트링 — 빈 값 생략, from>to는 swap 보정, limit 기본 50. */
export function buildListQuery({ from, to, source, status, limit } = {}) {
  let f = from || "";
  let t = to || "";
  if (f && t && f > t) {
    const tmp = f;
    f = t;
    t = tmp;
  }
  const p = new URLSearchParams();
  if (f) p.set("from", f);
  if (t) p.set("to", t);
  if (source) p.set("source", source);
  if (status) p.set("status", status);
  p.set("limit", String(limit || 50));
  return p.toString();
}

/** JWT payload에서 내 userId(삭제 링크 노출 판단용). 실패 시 null → 링크 미노출. */
export function myUserIdFromToken(tokenStr) {
  if (!tokenStr) return null;
  try {
    const b64 = tokenStr.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64));
    return payload.userId || payload.sub || null;
  } catch (e) {
    return null;
  }
}

/** RECEIVED/EXPIRED 만 한글 표기, 나머지는 원문(기존 화면 관례 유지). */
export function statusLabel(status) {
  if (status === "RECEIVED") return "분류 대기";
  if (status === "EXPIRED") return "만료";
  return status || "";
}

const TERMINAL = ["DONE", "FAILED", "CANCELLED", "EXPIRED", "RECEIVED"]; // RECEIVED=시작 전 폐기 허용

/** 삭제 가능 = 본인 잡 && 터미널 상태(서버 규칙의 UI 미러 — 서버가 최종 검증). */
export function canDelete(job, myUserId) {
  if (!job || !myUserId || job.userId !== myUserId) return false;
  return TERMINAL.includes(job.status);
}

/** 상태 필터 매칭 — LIVE=RUNNING+PENDING, FAILEDX=FAILED+CANCELLED+EXPIRED(클라이언트 합성). */
export function statusMatches(job, filter) {
  if (!filter) return true;
  if (filter === "LIVE")
    return job.status === "RUNNING" || job.status === "PENDING";
  if (filter === "FAILEDX")
    return (
      job.status === "FAILED" ||
      job.status === "CANCELLED" ||
      job.status === "EXPIRED"
    );
  return job.status === filter;
}

/** 합성 상태(LIVE/FAILEDX)는 서버에 안 보냄(클라이언트 필터) — 단일 상태만 전달. */
export function serverStatusParam(filter) {
  return filter === "LIVE" || filter === "FAILEDX" ? "" : filter || "";
}
